import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  MusicalNoteIcon,
  MapPinIcon,
  CalendarIcon,
  HeartIcon,
  UserGroupIcon,
  StarIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../widgets/Logo';
import { getRandomHeroImage } from '../../config/heroImages';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
`;

const Header = styled.header`
  background: ${props => props.theme.colors.surface};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.lg} 0;
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const AuthButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
`;

const Button = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  &.primary {
    background: ${props => props.theme.colors.gradient};
    color: white;
    
    &:hover {
      background: ${props => props.theme.colors.gradientHover};
    }
  }
  
  &.secondary {
    background: transparent;
    color: ${props => props.theme.colors.text.primary};
    border: 1px solid ${props => props.theme.colors.border};
    
    &:hover {
      background: ${props => props.theme.colors.surfaceVariant};
    }
  }
`;

const Hero = styled.section`
  padding: ${props => props.theme.spacing.xxl} 0;
  text-align: center;
  max-width: 100%;
  position: relative;
  background-image: url(${props => props.$bgImage});
  background-size: cover;
  background-position: center;
  color: white;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    z-index: 1;
  }
`;

const HeroContent = styled(motion.div)`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: ${props => props.theme.spacing.lg};
  color: white;
  line-height: 1.1;
  text-shadow: 2px 2px 8px rgba(0,0,0,0.7);
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.typography.h4.fontSize};
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: ${props => props.theme.spacing.xl};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const CTAButton = styled.button`
  background: ${props => props.theme.colors.gradient};
  color: white;
  border: none;
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xxl};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.typography.h4.fontSize};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin: 0 auto;
  box-shadow: 0 4px 20px rgba(0,0,0,0.4);
  
  &:hover {
    background: ${props => props.theme.colors.gradientHover};
    transform: translateY(-2px);
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const Features = styled.section`
  padding: ${props => props.theme.spacing.xxl} 0;
  background: ${props => props.theme.colors.surface};
  border-top: 1px solid ${props => props.theme.colors.border};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const FeaturesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
`;

const FeaturesTitle = styled.h2`
  text-align: center;
  font-size: ${props => props.theme.typography.h2.fontSize};
  font-weight: ${props => props.theme.typography.h2.fontWeight};
  margin-bottom: ${props => props.theme.spacing.xxl};
  color: ${props => props.theme.colors.text.primary};
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${props => props.theme.spacing.xl};
`;

const FeatureCard = styled(motion.div)`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  text-align: center;
`;

const FeatureIcon = styled.div`
  width: 64px;
  height: 64px;
  background: ${props => props.theme.colors.gradient};
  border-radius: ${props => props.theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${props => props.theme.spacing.lg};
  
  svg {
    width: 32px;
    height: 32px;
    color: white;
  }
`;

const FeatureTitle = styled.h3`
  font-size: ${props => props.theme.typography.h4.fontSize};
  font-weight: 600;
  margin-bottom: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.text.primary};
`;

const FeatureDescription = styled.p`
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.6;
`;

const Footer = styled.footer`
  padding: ${props => props.theme.spacing.xl} 0;
  text-align: center;
  color: ${props => props.theme.colors.text.muted};
  font-size: ${props => props.theme.typography.small.fontSize};
`;

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [bgImage, setBgImage] = React.useState('');

  React.useEffect(() => {
    const randomImage = getRandomHeroImage();
    setBgImage(randomImage);
  }, []);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/');
    } else {
      navigate('/register');
    }
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const features = [
    {
      icon: <MapPinIcon />,
      title: 'Discover Local Shows',
      description: 'Find live music events happening in your area. From intimate venues to major concerts, never miss a show again.'
    },
    {
      icon: <CalendarIcon />,
      title: 'Stay Updated',
      description: 'Get real-time updates on new shows, venue changes, and special events. Your music calendar, always current.'
    },
    {
      icon: <HeartIcon />,
      title: 'Save Your Favorites',
      description: 'Save shows you want to attend and build your personal concert wishlist. Never lose track of must-see performances.'
    },
    {
      icon: <UserGroupIcon />,
      title: 'Join the Scene',
      description: 'Connect with other music lovers in your area. Share recommendations, discover new artists, and build your local music community.'
    },
    {
      icon: <StarIcon />,
      title: 'Earn Scene Points',
      description: 'Contribute to the community by posting shows and helping others. Earn points and unlock exclusive features as you become a scene veteran.'
    },
    {
      icon: <MusicalNoteIcon />,
      title: 'Support Local Music',
      description: 'Help local bands and venues thrive by spreading the word about their shows. Your support keeps the local music scene alive.'
    }
  ];

  return (
    <Container>
      <Header>
        <Nav>
          <LogoContainer>
            <Logo size="large" />
          </LogoContainer>
          <AuthButtons>
            <Button className="secondary" onClick={handleSignIn}>
              Sign In
            </Button>
            <Button className="primary" onClick={handleGetStarted}>
              Get Started
            </Button>
          </AuthButtons>
        </Nav>
      </Header>

      <Hero $bgImage={bgImage}>
        <HeroContent
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <HeroTitle>
            Discover Live Music<br />
            <span style={{ 
              background: 'linear-gradient(45deg, #A78BFA, #F472B6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>In Your Scene</span>
          </HeroTitle>
          <HeroSubtitle>
            SceneFeed connects you with the best live music events in your area. 
            Find shows, save favorites, and join a community of music lovers who keep the local scene alive.
          </HeroSubtitle>
          <CTAButton onClick={handleGetStarted}>
            {isAuthenticated ? 'Go to Dashboard' : 'Join SceneFeed'}
            <ArrowRightIcon />
          </CTAButton>
        </HeroContent>
      </Hero>

      <Features>
        <FeaturesContainer>
          <FeaturesTitle>Why SceneFeed?</FeaturesTitle>
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <FeatureIcon>
                  {feature.icon}
                </FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </FeaturesContainer>
      </Features>

      <Footer>
        <p>© 2025 SceneFeed. Keeping local music scenes alive, one show at a time.</p>
      </Footer>
    </Container>
  );
};

export default LandingPage; 